import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { MessageService } from 'primeng/api';

import { InstituteService } from '../services/institute/institute.service';
import { SearchInstituteResponse } from '../models/institute/searchInstituteResponse';
import { RoiCalculatorService, RoiCalculationResponse } from '../services/roi-calculator/roi-calculator.service';
import { InstituteCoursesV2Response } from '../models/institute/instituteCoursesV2Response';

interface DropdownOption {
  name: string;
  code: string | number;
}

@Component({
  selector: 'app-public-roi-calculator',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    AutoCompleteModule,
    DropdownModule,
    ButtonModule,
    CheckboxModule,
    ToastModule,
    InputTextModule,
    RippleModule
  ],
  templateUrl: './public-roi-calculator.component.html',
  styleUrls: ['./public-roi-calculator.component.scss'],
  providers: [MessageService]
})
export class PublicRoiCalculatorComponent implements OnInit {
  roiForm!: FormGroup;
  isLoading = false;
  isLoadingCourses = false;
  currencySymbol = '$';
  selectedRoiYears = 10;
  
  universitySearchSuggestions: any[] = [];
  startYears: DropdownOption[] = [
    { name: '2026', code: 2026 },
    { name: '2027', code: 2027 },
    { name: '2028', code: 2028 }
  ];

  allCourses: InstituteCoursesV2Response[] = [];
  schoolsList: DropdownOption[] = [];
  coursesList: DropdownOption[] = [];

  hasScholarship = false;
  calculationResult: RoiCalculationResponse | null = null;
  selectedUniversityName = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private instituteService: InstituteService,
    private roiCalculatorService: RoiCalculatorService,
    private messageService: MessageService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Setup form change listeners for Level/University cascading
    this.roiForm.get('level')?.valueChanges.subscribe(() => {
      this.roiForm.get('university')?.setValue(null, { emitEvent: false });
      this.handleLevelOrUniversityChange();
    });

    this.roiForm.get('university')?.valueChanges.subscribe(() => {
      this.handleLevelOrUniversityChange();
    });

    // Listen to school changes to populate courses
    this.roiForm.get('school')?.valueChanges.subscribe(schoolOpt => {
      const courseCtrl = this.roiForm.get('course');
      courseCtrl?.setValue(null);
      
      const schoolName = schoolOpt?.code;
      if (schoolName && this.allCourses.length > 0) {
        const filteredCourses = this.allCourses
          .filter(c => c.majorOrStream && c.majorOrStream.includes(schoolName) && c.courseName)
          .map(c => c.courseName as string);
        
        const uniqueCourses = Array.from(new Set(filteredCourses)).sort();
        this.coursesList = uniqueCourses.map(c => ({ name: c, code: c }));
      } else {
        this.coursesList = [];
      }
    });
  }

  private initForm(): void {
    this.roiForm = this.fb.group({
      planToStart: [null, Validators.required],
      level: [null, Validators.required],
      university: [null, Validators.required],
      school: [null],
      course: [null],
      studentStatus: ['International', Validators.required],
      livingSituation: [null, Validators.required],
      scholarshipAmount: [null, [Validators.min(0)]],
      loanRecommendation: [false]
    });
  }

  handleLevelOrUniversityChange(): void {
    const lvl = this.roiForm.get('level')?.value;
    const uni = this.roiForm.get('university')?.value;

    if (uni && uni.name) {
      const name = uni.name.toLowerCase();
      if (name.includes('london') || name.includes('oxford') || name.includes('cambridge') || name.includes('lse') || name.includes('manchester') || name.includes('edinburgh') || name.includes('uk') || name.includes('united kingdom') || name.includes('imperial')) {
        this.currencySymbol = '£';
      } else {
        this.currencySymbol = '$';
      }
    } else {
      this.currencySymbol = '$';
    }

    const schoolCtrl = this.roiForm.get('school');
    const courseCtrl = this.roiForm.get('course');

    if (lvl === 'PG') {
      schoolCtrl?.setValidators([Validators.required]);
      courseCtrl?.setValidators([Validators.required]);
    } else {
      schoolCtrl?.clearValidators();
      courseCtrl?.clearValidators();
    }
    schoolCtrl?.updateValueAndValidity({ emitEvent: false });
    courseCtrl?.updateValueAndValidity({ emitEvent: false });

    if (lvl === 'PG' && uni && uni.id) {
      this.isLoadingCourses = true;
      this.roiCalculatorService.getRoiPgCourses(uni.id).subscribe({
        next: (response) => {
          this.isLoadingCourses = false;
          if (response && response.result) {
            this.allCourses = response.result;
            
            const schools = Array.from(new Set(
              this.allCourses
                .flatMap(c => c.majorOrStream || [])
                .filter((s): s is string => !!s)
            )).sort();

            this.schoolsList = schools.map(s => ({ name: s, code: s }));
            schoolCtrl?.setValue(null);
            courseCtrl?.setValue(null);
            this.coursesList = [];
          }
        },
        error: () => {
          this.isLoadingCourses = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error Loading Courses',
            detail: 'Could not fetch schools and courses for this university.'
          });
        }
      });
    } else {
      this.allCourses = [];
      this.schoolsList = [];
      this.coursesList = [];
      schoolCtrl?.setValue(null, { emitEvent: false });
      courseCtrl?.setValue(null, { emitEvent: false });
    }
  }

  searchUniversities(event: { query: string }): void {
    if (event.query.length >= 2) {
      const selectedLevel = this.roiForm.get('level')?.value || '';
      this.roiCalculatorService.searchRoiUniversities(event.query, selectedLevel).subscribe({
        next: (response: any[]) => {
          this.universitySearchSuggestions = response.map(u => ({
            id: u.id,
            name: u.name
          }));
        },
        error: () => {
          this.universitySearchSuggestions = [];
        }
      });
    } else {
      this.universitySearchSuggestions = [];
    }
  }

  toggleScholarship(secured: boolean): void {
    this.hasScholarship = secured;
    if (!secured) {
      this.roiForm.patchValue({ scholarshipAmount: 0 });
    }
  }

  calculateRoi(): void {
    if (this.roiForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Form Invalid',
        detail: 'Please select a valid university and fill all program details.'
      });
      return;
    }

    const formValues = this.roiForm.value;
    const selectedUni = formValues.university;

    if (!selectedUni || !selectedUni.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid University',
        detail: 'Please select a university from the suggestion list.'
      });
      return;
    }

    this.selectedUniversityName = selectedUni.name;
    this.isLoading = true;

    const request = {
      universityId: selectedUni.id,
      level: formValues.level,
      schoolName: formValues.level === 'PG' ? formValues.school?.code : undefined,
      courseName: formValues.level === 'PG' ? formValues.course?.code : undefined,
      programType: formValues.level === 'PG' ? formValues.course?.code : undefined,
      studentStatus: formValues.studentStatus,
      livingSituation: formValues.livingSituation,
      scholarshipAmount: Number(formValues.scholarshipAmount || 0),
      suggestLoan: formValues.loanRecommendation
    };

    const startTime = Date.now();
    this.roiCalculatorService.calculateRoi(request).subscribe({
      next: (response) => {
        const elapsedTime = Date.now() - startTime;
        const minimumDelay = 2000;
        const remainingDelay = Math.max(0, minimumDelay - elapsedTime);

        setTimeout(() => {
          this.isLoading = false;
          this.calculationResult = response;
          if (response.investment?.tuition?.local) {
            const firstChar = response.investment.tuition.local.trim().charAt(0);
            if (isNaN(Number(firstChar))) {
              this.currencySymbol = firstChar;
            }
          }
        }, remainingDelay);
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Calculation Failed',
          detail: err?.message || 'Failed to project returns. Please try again.'
        });
      }
    });
  }

  formatAxisValue(factor: number, isPositive: boolean = false): string {
    if (!this.calculationResult) return isPositive ? `+$120k` : `$0`;
    const totalInvestment = this.calculationResult.investment.totalInvestment.rawLocal || 120000;
    const value = totalInvestment * factor;
    return this.formatRawValue(value, isPositive);
  }

  formatRawValue(value: number, isProfit = false): string {
    const absVal = Math.abs(value);
    let formatted = '';
    
    if (absVal === 0) {
      return `${this.currencySymbol}0`;
    } else if (absVal >= 1000000) {
      formatted = `${(absVal / 1000000).toFixed(1)}M`;
    } else {
      formatted = `${Math.round(absVal / 1000)}k`;
    }
    
    const sign = value < 0 ? '-' : (value > 0 && isProfit ? '+' : '');
    return `${sign}${this.currencySymbol}${formatted}`;
  }

  getBreakEvenBounds(): { bottomVal: number, topVal: number } {
    if (!this.calculationResult) return { bottomVal: -120000, topVal: 180000 };
    const breakEven = this.calculationResult.returns.breakEvenYears || 0;
    const totalInvestment = this.calculationResult.investment.totalInvestment.rawLocal || 120000;
    
    const r = 0.05;
    let s0 = 0;
    if (breakEven > 0) {
      s0 = totalInvestment * r / (Math.pow(1 + r, breakEven) - 1);
    }
    
    const values = [];
    for (let t = 0; t <= 4; t++) {
      let val = -totalInvestment;
      if (t > 0 && breakEven > 0) {
        val = -totalInvestment + s0 * (Math.pow(1 + r, t) - 1) / r;
      }
      values.push(val);
    }
    
    const minVal = -totalInvestment;
    const maxVal = Math.max(...values);
    const topVal = Math.max(totalInvestment * 0.5, maxVal * 1.1);
    
    return { bottomVal: minVal, topVal };
  }

  getBreakEvenYLabels(): string[] {
    const { bottomVal, topVal } = this.getBreakEvenBounds();
    const range = topVal - bottomVal;
    const labels = [];
    for (let i = 5; i >= 0; i--) {
      const val = bottomVal + (i / 5) * range;
      labels.push(this.formatRawValue(val, val > 0));
    }
    return labels;
  }

  getBreakEvenPath(): string {
    if (!this.calculationResult) return '';
    const breakEven = this.calculationResult.returns.breakEvenYears || 0;
    const totalInvestment = this.calculationResult.investment.totalInvestment.rawLocal || 120000;
    
    const { bottomVal, topVal } = this.getBreakEvenBounds();
    const range = topVal - bottomVal;
    
    const points = [];
    const xCoords = [0, 162.5, 325, 487.5, 650];
    
    const r = 0.05;
    let s0 = 0;
    if (breakEven > 0) {
      s0 = totalInvestment * r / (Math.pow(1 + r, breakEven) - 1);
    }
    
    for (let t = 0; t <= 4; t++) {
      const x = xCoords[t];
      let val = -totalInvestment;
      if (t > 0 && breakEven > 0) {
        val = -totalInvestment + s0 * (Math.pow(1 + r, t) - 1) / r;
      }
      
      let y = 200 - ((val - bottomVal) / range) * 200;
      y = Math.max(0, Math.min(200, y));
      points.push(`${x} ${y.toFixed(1)}`);
    }
    
    return `M ${points[0]} L ${points.slice(1).join(' L ')}`;
  }

  navigateToSignup(): void {
    this.router.navigate(['/signup']);
  }

  navigateToSignin(): void {
    this.router.navigate(['/signin']);
  }
}
